/**
 * Dice Lab (Admin tools → Dice Lab): tune the 3D motion dice and test them.
 *
 * - **Values:** every number that sets the feel (`dice3d/tuning.ts`: physics, throw, phone and
 *   swipe input, timing, bad landing), one slider each, grouped. Edits apply to the next throw at
 *   once; Save keeps them in this browser (every roll shown on this device uses them), Undo
 *   Changes goes back to the saved values, All Defaults to the built-in ones. Closing with unsaved
 *   edits asks first, then drops them. Send Feedback attaches the saved values (`tuningSnapshot`).
 * - **Test throws:** the real tray (`Tray`), with made-up values: Throw puts the dice in your hand
 *   (shake, flick or swipe as in a game), Auto Throw throws at the chosen strength, Bot Throw makes a
 *   throw as for a bot (`tossFromSeq`). The tray stays open on the landed dice until you close
 *   the lab (the ✕ top right of the tray, as in a game, is there only until the first throw);
 *   Throw Again (along the bottom of the tray) repeats the last kind of throw (a fresh one).
 *   Auto Throw's lob (+) or drive (-) is set like its strength. Re-roll bad landings off: bad
 *   dice just glow red.
 * - **Dice type:** Shake/Fling or Popple, as in Options. Popple: the plastic board and the bubble;
 *   Throw puts the dice under it to press, Auto Throw pops it for you (a pop sets its own strength
 *   and lob: `popThrow`), Bot Throw plays under it as in a game. With fewer than 5 dice thrown the
 *   rest lie on the hold mat (random values).
 * - **Last throw:** what your hand did (flick or swipe, strength, lob or drive, direction:
 *   `HandThrow`), where each die ended (`DieEnd`), and why it was or wasn't bad.
 * - **Stats:** N headless throws (`engine.simulate`, nothing drawn) with the same dice and strength
 *   (Popple: pops, inside the bubble): how many had a bad landing.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DECK_COLORS, t, type DeckColor, type DiceToss } from '@kadice/shared';
import { Tray, type ArmSpec, type Playing, type ThrowReport } from '../dice3d/DiceTray';
import type { DieEnd, Pose } from '../dice3d/engine';
import { canShow3d } from '../dice3d/support';
import { newSeed, popThrow, rng, tossFromSeq, tossOf, tossOfThrow, type HandThrow } from '../dice3d/toss';
import {
  DEFAULT_TUNING,
  TUNING_FIELDS,
  TUNING_GROUPS,
  revertTuning,
  saveTuning,
  savedTuning,
  setTuning,
  tuning,
  type DiceTuning,
  type TuningKey,
} from '../dice3d/tuning';
import { anim } from '../speed';

/** The lab's rolls: a player id no real player has. */
const LAB_PLAYER = -999_999;
const STATS_RUNS = [50, 100, 250, 500] as const;
/** Headless throws per slice of work, so the page stays responsive. */
const STATS_SLICE = 5;
const FADE_MS = 300;

const loadEngine = () => import('../dice3d/engine');
const fmt = (n: number, d = 2) => n.toFixed(d).replace(/\.?0+$/, '') || '0';
const die = () => 1 + Math.floor(Math.random() * 6);
const DICE_TYPES = ['motion', 'popple'] as const;
type LabDice = (typeof DICE_TYPES)[number];

interface Stats {
  total: number;
  done: number;
  badThrows: number;
  badDice: number;
  dice: number;
  leaning: number;
  ms: number;
}

export function DiceLab({ name, haptics, onClose }: { name: string; haptics: boolean; onClose: () => void }) {
  const [draft, setDraft] = useState<DiceTuning>(() => ({ ...tuning() }));
  const [stored, setStored] = useState<DiceTuning>(() => ({ ...savedTuning() }));
  const [note, setNote] = useState<string | null>(null);
  const [count, setCount] = useState(5);
  const [color, setColor] = useState<DeckColor>('blue');
  const [type, setType] = useState<LabDice>('motion');
  const popple = type === 'popple';
  const [strength, setStrength] = useState(0.5);
  const [loft, setLoft] = useState(0);
  const [last, setLast] = useState<'hand' | 'auto' | 'bot' | null>(null);
  const [read, setRead] = useState<HandThrow | null>(null);
  const [randomStrength, setRandomStrength] = useState(false);
  const [reroll, setReroll] = useState(true);
  const [arming, setArming] = useState<(ArmSpec & { seed: number }) | null>(null);
  const [playing, setPlaying] = useState<Playing | null>(null);
  // The dice have landed: the tray stays open on them until Close.
  const [landed, setLanded] = useState(false);
  const [reports, setReports] = useState<ThrowReport[]>([]);
  const [runs, setRuns] = useState<number>(100);
  const [stats, setStats] = useState<Stats | null>(null);
  const seq = useRef(0);
  const thrown = useRef<Pose[] | undefined>(undefined);
  const stopStats = useRef(false);
  const can3d = canShow3d();
  const unsaved = TUNING_FIELDS.some((f) => draft[f.key] !== stored[f.key]);

  // The values in use follow the sliders; leaving the lab drops anything unsaved.
  useEffect(() => setTuning(draft), [draft]);
  useEffect(
    () => () => {
      stopStats.current = true;
      revertTuning();
    },
    [],
  );

  const close = () => {
    if (unsaved && !window.confirm(t('diceLab.discard'))) return;
    onClose();
  };
  const set = (key: TuningKey, value: number) => {
    setNote(null);
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const save = () => {
    setTuning(draft);
    const ok = saveTuning();
    setStored({ ...savedTuning() });
    setNote(t(ok ? 'diceLab.saved' : 'diceLab.saveFailed'));
  };

  const pickStrength = () => (randomStrength ? 0.2 + Math.random() * 0.7 : strength);
  /** An auto throw (Popple: a pop) from this seed. */
  const autoToss = (seed: number): DiceToss => {
    if (popple) return tossOfThrow(popThrow(seed), seed);
    const r = rng(seed);
    return tossOf(pickStrength(), { x: (r() - 0.5) * 1.2, y: 1 }, [0, 0, 0], seed, loft);
  };
  // Popple: the dice not thrown lie on the hold mat, as if held (kept for the hand's throw).
  const held = useRef<number[]>([]);
  const play = (toss: DiceToss, mine: boolean, start?: Pose[]) => {
    const n = ++seq.current;
    if (!mine) held.current = Array.from({ length: 5 - count }, die);
    setReports([]);
    setLanded(false);
    setPlaying({
      roll: { seq: n, playerId: LAB_PLAYER, count, war: false, animate: true, toss },
      values: Array.from({ length: count }, die),
      color,
      name,
      held: held.current,
      toss,
      mine,
      ms: anim(tuning().rollMs),
      start,
    });
  };
  const throwByHand = () => {
    thrown.current = undefined;
    setPlaying(null);
    setLanded(false);
    setLast('hand');
    setRead(null);
    setReports([]);
    held.current = Array.from({ length: 5 - count }, die);
    setArming({
      count,
      held: held.current,
      seed: newSeed(),
      send: (toss) => {
        setArming(null);
        if (toss) play(toss, true, thrown.current);
        return Promise.resolve(true);
      },
    });
  };
  const autoThrow = () => {
    const seed = newSeed();
    setLast('auto');
    setRead(null);
    play(autoToss(seed), false);
  };
  const botThrow = () => {
    setLast('bot');
    setRead(null);
    play(tossFromSeq(newSeed(), LAB_PLAYER), false);
  };
  const again = () => (last === 'hand' ? throwByHand() : last === 'auto' ? autoThrow() : botThrow());
  const closeTray = () => {
    setPlaying(null);
    setArming(null);
    setLanded(false);
  };

  const runStats = async () => {
    const engine = await loadEngine();
    stopStats.current = false;
    const size = engine.traySize(window.innerWidth, window.innerHeight);
    const dome = popple ? engine.poppleLayout(size).dome : null;
    const tu = tuning();
    const seconds = Math.max(600, tu.rollMs - tu.holdMs - FADE_MS) / 1000;
    const s: Stats = { total: runs, done: 0, badThrows: 0, badDice: 0, dice: 0, leaning: 0, ms: 0 };
    setStats({ ...s });
    const t0 = performance.now();
    while (s.done < runs && !stopStats.current) {
      for (let k = 0; k < STATS_SLICE && s.done < runs; k++) {
        const rec = engine.simulate(autoToss(newSeed()), count, size, seconds, undefined, [], [], dome);
        s.done++;
        s.dice += count;
        s.badDice += rec.bad.length;
        if (rec.bad.length > 0) s.badThrows++;
        s.leaning += rec.ends.filter((e) => e.on && !e.bad).length;
      }
      s.ms = (performance.now() - t0) / s.done;
      setStats({ ...s });
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  };

  const pct = (a: number, b: number) => fmt(b > 0 ? (100 * a) / b : 0, 1);

  // The tray is a sibling of the backdrop, not inside it: its taps must not reach the backdrop's close.
  return (
    <>
      <div className="modal-backdrop" onClick={close}>
        <div
          className="modal dice-lab"
          role="dialog"
          aria-modal="true"
          aria-label={t('diceLab.title')}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="row">
            <h3>{t('diceLab.title')}</h3>
            {unsaved && <span className="badge">{t('diceLab.unsaved')}</span>}
            <span className="spacer" />
            <button onClick={close}>{t('common.close')}</button>
          </div>
          <p className="muted small">{t('diceLab.intro')}</p>
          <div className="row dice-lab-actions">
            <button className="primary" onClick={save} disabled={!unsaved}>
              {t('diceLab.save')}
            </button>
            <button onClick={() => setDraft({ ...stored })} disabled={!unsaved}>
              {t('diceLab.revert')}
            </button>
            <button onClick={() => setDraft({ ...DEFAULT_TUNING })}>{t('diceLab.defaults')}</button>
            {note && <span className="muted small">{note}</span>}
          </div>

          <section className="dice-lab-test">
            <h4>{t('diceLab.test')}</h4>
            {!can3d && <p className="muted">{t('diceLab.no3d')}</p>}
            <div className="dice-lab-field">
              <span>{t('diceLab.type')}</span>
              <div className="dice-lab-choice" role="group" aria-label={t('diceLab.type')}>
                {DICE_TYPES.map((d) => (
                  <button
                    key={d}
                    className={d === type ? 'on' : undefined}
                    disabled={!!playing || !!arming}
                    onClick={() => setType(d)}
                  >
                    {t(`diceLab.type.${d}`)}
                  </button>
                ))}
              </div>
              {popple && <small className="muted">{t('diceLab.typeHint')}</small>}
            </div>
            <Choice label={t('diceLab.dice')} value={count} options={[1, 2, 3, 4, 5]} onChange={setCount} />
            <label className="dice-lab-field">
              <span>{t('diceLab.deck')}</span>
              <select value={color} onChange={(e) => setColor(e.target.value as DeckColor)}>
                {DECK_COLORS.map((c) => (
                  <option key={c} value={c}>
                    {t(`color.${c}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="dice-lab-field">
              <span>
                {t('diceLab.strength')} <strong>{fmt(strength)}</strong>
              </span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={strength}
                disabled={randomStrength || popple}
                onChange={(e) => setStrength(Number(e.target.value))}
              />
            </label>
            <label className="dice-lab-field">
              <span>
                {t('diceLab.loft')} <strong>{fmt(loft)}</strong>
              </span>
              <input
                type="range"
                min={-1}
                max={1}
                step={0.05}
                value={loft}
                disabled={popple}
                onChange={(e) => setLoft(Number(e.target.value))}
              />
              <small className="muted">{t('diceLab.loftHint')}</small>
            </label>
            <label className="dice-lab-check">
              <input
                type="checkbox"
                checked={randomStrength}
                disabled={popple}
                onChange={(e) => setRandomStrength(e.target.checked)}
              />
              {t('diceLab.randomStrength')}
            </label>
            <label className="dice-lab-check">
              <input type="checkbox" checked={reroll} onChange={(e) => setReroll(e.target.checked)} />
              {t('diceLab.reroll')}
            </label>
            <div className="row dice-lab-actions">
              <button className="primary" disabled={!can3d || !!playing || !!arming} onClick={throwByHand}>
                {t(popple ? 'diceLab.throwPop' : 'diceLab.throw')}
              </button>
              <button disabled={!can3d || !!playing || !!arming} onClick={autoThrow}>
                {t(popple ? 'diceLab.autoPop' : 'diceLab.auto')}
              </button>
              <button disabled={!can3d || !!playing || !!arming} onClick={botThrow}>
                {t('diceLab.botThrow')}
              </button>
            </div>
            <h4>{t('diceLab.lastThrow')}</h4>
            {read && <p className="small">{readout(read)}</p>}
            {reports.length === 0 ? (
              <p className="muted small">{t('diceLab.noThrow')}</p>
            ) : (
              reports.map((r) => (
                <div key={`${r.seq}:${r.attempt}`} className="dice-lab-report">
                  <strong>{r.attempt === 0 ? t('diceLab.firstThrow') : t('diceLab.attempt', { n: r.attempt })}</strong>
                  <ul>
                    {r.ends.map((e, i) => (
                      <li key={i} className={e.bad ? 'bad' : undefined}>
                        {t('diceLab.die', { n: i + 1 })}: {where(e)}
                        {e.bad ? ` · ${t('diceLab.bad')}` : e.on ? ` · ${t('diceLab.leaning')}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>

          <section className="dice-lab-stats">
            <h4>{t('diceLab.stats')}</h4>
            <p className="muted small">{t('diceLab.statsHint')}</p>
            <div className="row dice-lab-actions">
              <Choice label={t('diceLab.runs')} value={runs} options={[...STATS_RUNS]} onChange={setRuns} />
              <button
                className="primary"
                disabled={!!stats && stats.done < stats.total}
                onClick={() => void runStats()}
              >
                {t('diceLab.run')}
              </button>
            </div>
            {stats && (
              <ul className="dice-lab-results">
                {stats.done < stats.total && <li>{t('diceLab.running', { done: stats.done, total: stats.total })}</li>}
                <li>
                  {t('diceLab.statsThrows', {
                    bad: stats.badThrows,
                    total: stats.done,
                    pct: pct(stats.badThrows, stats.done),
                  })}
                </li>
                <li>
                  {t('diceLab.statsDice', {
                    bad: stats.badDice,
                    total: stats.dice,
                    pct: pct(stats.badDice, stats.dice),
                  })}
                </li>
                <li>{t('diceLab.statsLeaning', { n: stats.leaning })}</li>
                <li>{t('diceLab.statsTime', { ms: fmt(stats.ms, 1) })}</li>
              </ul>
            )}
          </section>

          {TUNING_GROUPS.map((g) => (
            <section key={g} className="dice-lab-group">
              <h4>{t(`diceLab.group.${g}`)}</h4>
              {TUNING_FIELDS.filter((f) => f.group === g).map((f) => (
                <Field
                  key={f.key}
                  as={f.choice ? 'div' : 'label'}
                  className={`dice-lab-field${draft[f.key] !== DEFAULT_TUNING[f.key] ? ' changed' : ''}`}
                >
                  <span>
                    {t(`diceLab.f.${f.key}`)} <strong>{fmt(draft[f.key], 3)}</strong>
                    {draft[f.key] !== DEFAULT_TUNING[f.key] && (
                      <button
                        type="button"
                        className="link small"
                        onClick={(e) => {
                          e.preventDefault();
                          set(f.key, DEFAULT_TUNING[f.key]);
                        }}
                      >
                        {t('diceLab.resetOne', { value: fmt(DEFAULT_TUNING[f.key], 3) })}
                      </button>
                    )}
                  </span>
                  {f.choice ? (
                    <div className="dice-lab-choice" role="group" aria-label={t(`diceLab.f.${f.key}`)}>
                      {Array.from(
                        { length: Math.round((f.max - f.min) / f.step) + 1 },
                        (_, i) => f.min + i * f.step,
                      ).map((v) => (
                        <button
                          key={v}
                          type="button"
                          className={v === draft[f.key] ? 'on' : undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            set(f.key, v);
                          }}
                        >
                          {t(`diceLab.v.${f.key}.${v}`)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="range"
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      value={draft[f.key]}
                      onChange={(e) => set(f.key, Number(e.target.value))}
                    />
                  )}
                  <small className="muted">{t(`diceLab.h.${f.key}`)}</small>
                </Field>
              ))}
            </section>
          ))}
        </div>
      </div>
      {can3d &&
        createPortal(
          <Tray
            arming={arming}
            playing={playing}
            color={color}
            popple={popple}
            haptics={haptics}
            rethrowBad={reroll}
            stay
            actions={
              playing && (
                <button className="primary" disabled={!landed} onClick={again}>
                  {t('diceLab.again')}
                </button>
              )
            }
            onClose={closeTray}
            onCancel={() => setArming(null)}
            onThrown={(x) => {
              thrown.current = x.poses;
              setRead(x.read);
            }}
            onFailed={() => setArming(null)}
            onDone={(n) => n === seq.current && setLanded(true)}
            onReport={(r) => setReports((list) => [...list.filter((x) => x.seq === r.seq), r])}
          />,
          document.body,
        )}
    </>
  );
}

/** What your hand did, in words. */
function readout(h: HandThrow): string {
  const loft =
    h.loft > 0.05
      ? t('diceLab.lob', { n: fmt(h.loft) })
      : h.loft < -0.05
        ? t('diceLab.drive', { n: fmt(-h.loft) })
        : t('diceLab.level');
  const angle = Math.round((Math.atan2(h.dir.x, h.dir.y) * 180) / Math.PI);
  const from = t(`diceLab.from.${h.pop ? 'pop' : h.from}`);
  return t('diceLab.read', { from, strength: fmt(h.strength), loft, angle });
}

/** Where a die ended, in words. */
function where(e: DieEnd): string {
  if (!e.on) return t('diceLab.onFloor', { tilt: Math.round(e.tilt) });
  const vars = { other: e.on.index + 1, y: fmt(e.y), offset: fmt(e.on.offset) };
  return t(e.on.kind === 'die' ? 'diceLab.onDie' : 'diceLab.onLying', vars);
}

/** A value's row: a label around its slider, or a plain box around a row of buttons (a label would click the first). */
function Field({ as, className, children }: { as: 'div' | 'label'; className: string; children: ReactNode }) {
  const Tag = as;
  return <Tag className={className}>{children}</Tag>;
}

/** A row of buttons, one per option. */
function Choice<T extends number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="dice-lab-field">
      <span>{label}</span>
      <div className="dice-lab-choice" role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o} className={o === value ? 'on' : undefined} onClick={() => onChange(o)}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
